import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useTherapist } from "../../../context/useTherapist";

import { PlusSubscriptionAPI } from "../infrastructure/api/PlusSubscription.api";
import {
    BillingPeriod,
    CancellationReason,
    PlanTier,
    PlusSubscriptionState,
    Quote,
    toCoupon,
    toPlusSubscriptionState,
    toQuote,
} from "../domain/types";

export const PLUS_SUBSCRIPTION_KEY = "plus-subscription";

/**
 * The account's Plus subscription: what it is, and what may be done to it.
 *
 * Every action refetches rather than patching the cache optimistically. The
 * plan can change underneath a cancel or a resume — a centre seat, a webhook
 * landing mid-click — and a screen that disagrees with what the account can
 * actually do is worse than a spinner.
 */
export const usePlusSubscription = () =>
    useQuery<PlusSubscriptionState>({
        queryKey: [PLUS_SUBSCRIPTION_KEY],
        queryFn: async () => {
            const { data } = await PlusSubscriptionAPI.state();
            return toPlusSubscriptionState(data);
        },
    });

/**
 * The period this account is billed in.
 *
 * What anything sold on top of the plan has to be priced in: a module joins
 * the subscription that is already running, so a yearly subscription is
 * charged the yearly price for it. Monthly for everybody else, including an
 * account with no subscription at all — there is nothing yearly to join.
 */
export const useBillingPeriod = (): BillingPeriod => {
    const { data } = usePlusSubscription();
    return data?.subscription?.billingPeriod ?? "monthly";
};

/**
 * Start a purchase.
 *
 * Fetches the Checkout URL over the authenticated client, then navigates the
 * whole page to it: Stripe's Checkout cannot live in an XHR response, and the
 * endpoint cannot be a plain redirect target because a top-level navigation
 * would carry no auth header.
 */
export const useStartPlusCheckout = () =>
    useMutation({
        mutationFn: async ({
            period,
            coupon,
            modules = [],
            tier = "plus",
        }: {
            period: BillingPeriod;
            coupon?: string;
            /** Paid modules taken up in this same purchase. */
            modules?: string[];
            /** Plus or Max. */
            tier?: PlanTier;
        }) => {
            const { data } = await PlusSubscriptionAPI.checkout(period, coupon, modules, tier);
            window.location.assign(data.url);
        },
    });

/**
 * What the purchase costs with these modules ticked, and with this code.
 *
 * A query keyed by the selection, so ticking a box the reader has already
 * tried is instant and going back to it asks nothing. The arithmetic stays on
 * the server: the annual discount is applied per line, and a total added up
 * here would be a second opinion about somebody's invoice.
 *
 * `enabled` off while there is nothing to buy — an account that already pays,
 * or a deployment that sells nothing — so the screen asks for no quote it
 * would not show.
 */
export const usePlusQuote = (
    period: BillingPeriod,
    modules: string[],
    coupon?: string,
    enabled = true,
    tier: PlanTier = "plus"
) => {
    // Sorted, so ticking A then B and B then A are one cache entry.
    const selection = [...modules].sort();
    return useQuery<Quote>({
        queryKey: [
            PLUS_SUBSCRIPTION_KEY,
            "quote",
            period,
            selection.join(","),
            coupon ?? "",
            tier,
        ],
        queryFn: async () => {
            const { data } = await PlusSubscriptionAPI.quote(period, selection, coupon, tier);
            return toQuote(data);
        },
        enabled,
    });
};

/**
 * Check a discount code before paying.
 *
 * Not a query: it is an action somebody takes on a code they typed, and
 * caching a yes would promise a discount the checkout could then refuse — a
 * coupon can run out between one screen and the next.
 */
export const useCheckCoupon = () =>
    useMutation({
        mutationFn: async (code: string) => {
            const { data } = await PlusSubscriptionAPI.coupon(code);
            return toCoupon(data);
        },
    });

/** Stop renewing. Plus stays until the period already paid for ends. */
export const useCancelPlusSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (why: { reason?: CancellationReason; comment?: string } | void) =>
            PlusSubscriptionAPI.cancel(why?.reason, why?.comment),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [PLUS_SUBSCRIPTION_KEY] });
            // The plan itself may have moved, and the whole app gates on it.
            queryClient.invalidateQueries({ queryKey: ["therapist"] });
        },
    });
};

/** Undo a pending cancellation, on the subscription already held. */
export const useResumePlusSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => PlusSubscriptionAPI.resume(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [PLUS_SUBSCRIPTION_KEY] });
            queryClient.invalidateQueries({ queryKey: ["therapist"] });
        },
    });
};

/**
 * Move a live subscription between Plus and Max.
 *
 * The therapist is reloaded too. It lives in a context rather than in the
 * query cache, so invalidating a query key would leave it as it was — and
 * the plan's name beside the avatar and the monthly session allowance both
 * come from it, and both change with the size.
 */
export const useChangePlusTier = () => {
    const queryClient = useQueryClient();
    const { refreshTherapist } = useTherapist();
    return useMutation({
        mutationFn: (tier: PlanTier) => PlusSubscriptionAPI.changeTier(tier),
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: [PLUS_SUBSCRIPTION_KEY] });
            await refreshTherapist();
        },
    });
};

/**
 * Card, invoices and receipts — all of which live at Stripe, not here.
 * Called with `true`, the portal opens on changing the card.
 */
export const useOpenBillingPortal = () =>
    useMutation({
        mutationFn: async (updatePaymentMethod: boolean | void) => {
            const { data } = await PlusSubscriptionAPI.billingPortal(Boolean(updatePaymentMethod));
            window.location.assign(data.url);
        },
    });
